/**
 * MechanicPartsPanel — Compact parts status and action panel for mechanic workflow.
 *
 * Shows per-work-order part statuses (needed / ordered / arrived, plus the
 * server effective status on needed rows — backordered and dismissed included)
 * and provides quick actions (#3568):
 *   • Request a part (sends a parts-request note visible to foreman/service advisor)
 *   • Order parts (only for roles that manage POs: manager, service_advisor, admin, owner)
 *   • Mark parts installed / leftover (mechanic-facing consumption dialog)
 *
 * Foreman / purchasing-only PO management actions are intentionally suppressed
 * or shown as disabled chips so mechanics are not routed through the PO UI.
 */

import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InventoryIcon from '@mui/icons-material/Inventory';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import RecyclingIcon from '@mui/icons-material/Recycling';
import BlockIcon from '@mui/icons-material/Block';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import SendIcon from '@mui/icons-material/Send';
import BookmarkAddIcon from '@mui/icons-material/BookmarkAdd';
import UndoIcon from '@mui/icons-material/Undo';
import { useWorkOrderPartsStatus, type PartsStatusResponse } from '@/hooks/useWorkOrders';
import { useAddBookingNote } from '@/hooks/useBookingNotes';
import {
  useReservePartForWorkOrder,
  useReleasePartReservation,
  useConsumePartReservation,
} from '@/hooks/useInventory';
import { PartsConsumptionDialog } from './PartsConsumptionDialog';
import { OrderPartsDialog } from './OrderPartsDialog';
import { PartNeedReserveDialog } from '@/components/inventory';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { useAuthStore } from '@/stores/authStore';
import { useNotify } from '@/stores/notificationStore';
import { ApiError } from '@/lib/api';
import { formatDate } from '@/utils/locale';

// ─── Status types & helpers ───────────────────────────────────────────────────

export type MechanicPartStatus =
  | 'needed'
  | 'reserved'
  | 'ordered'
  | 'backordered'
  | 'arrived'
  | 'installed'
  | 'dismissed'
  | 'leftover';

const STATUS_COLOR: Record<
  MechanicPartStatus,
  'default' | 'warning' | 'info' | 'success' | 'error' | 'primary'
> = {
  needed: 'warning',
  reserved: 'info',
  ordered: 'primary',
  backordered: 'warning', // supplier delay, warning chip (#3568)
  arrived: 'success',
  installed: 'success',
  dismissed: 'default',
  leftover: 'default',
};

interface PartStatusChipProps {
  status: MechanicPartStatus;
}

function PartStatusChip({ status }: PartStatusChipProps) {
  const { t } = useTranslation('workorders');
  const icons: Record<MechanicPartStatus, ReactElement> = {
    needed: <AddShoppingCartIcon sx={{ fontSize: 14 }} />,
    reserved: <InventoryIcon sx={{ fontSize: 14 }} />,
    ordered: <LocalShippingIcon sx={{ fontSize: 14 }} />,
    backordered: <HourglassEmptyIcon sx={{ fontSize: 14 }} />,
    arrived: <CheckCircleOutlineIcon sx={{ fontSize: 14 }} />,
    installed: <CheckCircleOutlineIcon sx={{ fontSize: 14 }} />,
    dismissed: <BlockIcon sx={{ fontSize: 14 }} />,
    leftover: <RecyclingIcon sx={{ fontSize: 14 }} />,
  };
  return (
    <Chip
      size="small"
      icon={icons[status]}
      label={t(`mechanicParts.status.${status}`, status)}
      color={STATUS_COLOR[status]}
      variant="outlined"
      sx={{ height: 22, fontSize: '0.7rem' }}
    />
  );
}

// ─── Request-part note dialog ────────────────────────────────────────────────

interface RequestPartDialogProps {
  open: boolean;
  onClose: () => void;
  workOrderId: string;
}

function RequestPartDialog({ open, onClose, workOrderId }: RequestPartDialogProps) {
  const { t } = useTranslation('workorders');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const addNote = useAddBookingNote();
  const [message, setMessage] = useState('');

  const handleSend = () => {
    if (!message.trim()) return;
    addNote.mutate(
      { bookingId: workOrderId, category: 'internal', content: `[PART REQUEST] ${message.trim()}` },
      {
        onSuccess: () => {
          notify.success(t('mechanicParts.requestSentSuccess', 'Part request sent'));
          setMessage('');
          onClose();
        },
        onError: () => {
          notify.error(tc('error'), t('mechanicParts.requestSentError', 'Failed to send request'));
        },
      },
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('mechanicParts.requestDialog.title', 'Request a Part')}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t(
            'mechanicParts.requestDialog.description',
            'Describe the part you need. Your foreman or service advisor will see this request.',
          )}
        </Typography>
        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={3}
          label={t('mechanicParts.requestDialog.messageLabel', 'Part description / request')}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder={t(
            'mechanicParts.requestDialog.messagePlaceholder',
            'E.g. "Need front brake pads for BMW E90 — part number LPR05089"',
          )}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{tc('cancel')}</Button>
        <Button
          variant="contained"
          onClick={handleSend}
          disabled={!message.trim() || addNote.isPending}
          startIcon={addNote.isPending ? <CircularProgress size={16} /> : <SendIcon />}
        >
          {t('mechanicParts.requestDialog.send', 'Send request')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export interface MechanicPartsPanelProps {
  workOrderId: string;
  /** Wrap in a collapsible header (useful in compact task-card context). */
  collapsible?: boolean;
  /** Start collapsed. Only applies when collapsible=true. */
  defaultCollapsed?: boolean;
}

export function MechanicPartsPanel({
  workOrderId,
  collapsible = false,
  defaultCollapsed = false,
}: MechanicPartsPanelProps) {
  const { t } = useTranslation('workorders');
  const { t: tc } = useTranslation('common');
  const hasRole = useAuthStore((s) => s.hasRole);
  const notify = useNotify();

  // Roles that can manage purchase orders; mechanic role stays out of PO flow
  const canOrderParts = hasRole(['owner', 'admin', 'manager', 'service_advisor']);
  // All authenticated users can consume/install, reserve, or request
  const canConsume = hasRole(['mechanic', 'owner', 'admin', 'manager', 'service_advisor']);
  const canReserve = hasRole(['mechanic', 'owner', 'admin', 'manager', 'service_advisor']);

  const [expanded, setExpanded] = useState(!defaultCollapsed);
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [consumeDialogOpen, setConsumeDialogOpen] = useState(false);
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [reservingPart, setReservingPart] = useState<PartsStatusResponse['neededParts'][number] | null>(null);
  const [installReservation, setInstallReservation] = useState<PartsStatusResponse['reservations'][number] | null>(
    null,
  );

  const { data: partsStatus, isLoading } = useWorkOrderPartsStatus(workOrderId);
  const reservePart = useReservePartForWorkOrder();
  const releaseReservation = useReleasePartReservation();
  const consumeReservation = useConsumePartReservation();

  const handleReserveSubmit = (quantity: number) => {
    if (!reservingPart) return;
    reservePart.mutate(
      { bookingId: workOrderId, partNumber: reservingPart.partSku, quantity },
      {
        onSuccess: () => {
          notify.success(t('mechanicParts.reserveSuccess', 'Part reserved'));
          setReservingPart(null);
        },
        onError: (error: unknown) => {
          const reason =
            error instanceof ApiError && error.data && typeof error.data === 'object' && 'reason' in error.data
              ? (error.data as { reason?: string }).reason
              : undefined;
          notify.error(
            tc('error'),
            reason === 'insufficient_stock'
              ? t('mechanicParts.reserveInsufficientStock', 'Not enough stock available to reserve this quantity')
              : t('mechanicParts.reserveError', 'Failed to reserve part'),
          );
        },
      },
    );
  };

  const handleInstallConfirm = () => {
    if (!installReservation) return;
    consumeReservation.mutate(
      { bookingId: workOrderId, reservationId: installReservation.reservationId },
      {
        onSuccess: () => {
          notify.success(t('mechanicParts.installSuccess', 'Part marked installed'));
          setInstallReservation(null);
        },
        onError: () => {
          notify.error(tc('error'), t('mechanicParts.installError', 'Failed to mark part installed'));
          setInstallReservation(null);
        },
      },
    );
  };

  const handleRelease = (reservationId: string) => {
    releaseReservation.mutate(
      { bookingId: workOrderId, reservationId },
      {
        onSuccess: () => notify.success(t('mechanicParts.releaseSuccess', 'Reservation released')),
        onError: () => notify.error(tc('error'), t('mechanicParts.releaseError', 'Failed to release reservation')),
      },
    );
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
        <CircularProgress size={16} />
        <Typography variant="caption" color="text.secondary">
          {t('mechanicParts.loading', 'Loading parts…')}
        </Typography>
      </Box>
    );
  }

  if (!partsStatus) return null;

  const { neededParts, onOrderParts } = partsStatus;

  // Split on-order parts into fully-arrived and still-in-transit
  const arrivedParts = onOrderParts.filter(
    (p) => p.quantityReceived >= p.quantityOrdered,
  );
  const inTransitParts = onOrderParts.filter(
    (p) => p.quantityReceived < p.quantityOrdered,
  );

  const hasReservations = partsStatus.reservations.length > 0;
  const hasAnyParts =
    neededParts.length > 0 || onOrderParts.length > 0 || hasReservations || partsStatus.consumableParts.length > 0;

  if (!hasAnyParts) {
    return (
      <Box sx={{ py: 1 }}>
        <Typography variant="caption" color="text.secondary">
          {t('mechanicParts.noPartsRequired', 'No parts required for this work order.')}
        </Typography>
      </Box>
    );
  }

  // Parts awaiting a consumption confirmation: the work order's own
  // work_order_parts rows. Their id is the one the confirm route accepts —
  // never a purchase-order line id or a booking-service part id (#3270).
  const consumableParts = partsStatus.consumableParts.map((p) => ({
    id: p.workOrderPartId,
    partName: p.partName,
    partSku: p.partSku,
    quantity: p.quantity,
  }));

  const panelContent = (
    <Box data-testid="mechanic-parts-panel-content">
      {/* ── Needed (not yet ordered) ────────────────────── */}
      {neededParts.length > 0 && (
        <Box sx={{ mb: 1.5 }}>
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.75 }}>
            <AddShoppingCartIcon sx={{ fontSize: 14, color: 'warning.main' }} />
            <Typography
              variant="caption"
              fontWeight={700}
              color="warning.main"
              textTransform="uppercase"
            >
              {t('mechanicParts.sections.needed', 'Needed')}
            </Typography>
            <Chip
              size="small"
              label={neededParts.length}
              color="warning"
              sx={{ height: 18, fontSize: '0.65rem' }}
            />
          </Stack>

          <Stack spacing={0.5}>
            {neededParts.map((part) => (
              <Stack
                key={part.id}
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={1}
                sx={{
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  bgcolor: (theme) =>
                    theme.palette.mode === 'dark'
                      ? 'rgba(237,108,2,0.08)'
                      : 'warning.50',
                  border: '1px solid',
                  borderColor: 'warning.200',
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" noWrap>
                    {part.partName}
                  </Typography>
                  {/* Hand-set status wins; name the purchase-order line when it disagrees (#3568). */}
                  {part.orderStatus != null &&
                    part.purchaseOrderStatus != null &&
                    part.orderStatus !== part.purchaseOrderStatus && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {t('partOrderStatus.purchaseOrderHint', {
                          status: t(
                            `mechanicParts.status.${part.purchaseOrderStatus}`,
                            part.purchaseOrderStatus,
                          ),
                          defaultValue: 'Purchase order: {{status}}',
                        })}
                      </Typography>
                    )}
                  {part.partSku && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontFamily="monospace"
                    >
                      {part.partSku}
                    </Typography>
                  )}
                  {part.partId && part.availableStock !== null && (
                    <Typography
                      variant="caption"
                      display="block"
                      color={part.availableStock >= part.quantity ? 'success.main' : 'text.secondary'}
                    >
                      {t('mechanicParts.availableCount', {
                        count: part.availableStock,
                        defaultValue: '{{count}} in stock',
                      })}
                    </Typography>
                  )}
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ flexShrink: 0 }}
                >
                  ×{part.quantity}
                </Typography>
                {/* Older servers omit status; those rows stay needed (#3568). */}
                <PartStatusChip status={part.status ?? 'needed'} />
                {canReserve && part.partId && part.partSku && (
                  <Tooltip title={t('mechanicParts.actions.reservePart', 'Reserve from stock')}>
                    <IconButton
                      size="small"
                      onClick={() => { setReservingPart(part); }}
                      aria-label={t('mechanicParts.actions.reservePart')}
                      data-testid={`reserve-part-btn-${part.id}`}
                    >
                      <BookmarkAddIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            ))}
          </Stack>

          {/* Actions row: differs by role */}
          <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
            {/* Mechanic: request part via note */}
            <Button
              size="small"
              variant="outlined"
              color="warning"
              startIcon={<SendIcon />}
              onClick={() => setRequestDialogOpen(true)}
              sx={{ fontSize: '0.75rem' }}
              data-testid="request-part-btn"
            >
              {t('mechanicParts.actions.requestPart', 'Request part')}
            </Button>

            {/* Manager/admin: order via PO flow */}
            {canOrderParts && (
              <Button
                size="small"
                variant="outlined"
                color="primary"
                startIcon={<AddShoppingCartIcon />}
                onClick={() => setOrderDialogOpen(true)}
                sx={{ fontSize: '0.75rem' }}
                data-testid="order-parts-btn"
              >
                {t('mechanicParts.actions.orderParts', 'Order parts (PO)')}
              </Button>
            )}

            {/* Hint for pure mechanics: PO is done by someone else */}
            {!canOrderParts && (
              <Tooltip
                title={t(
                  'mechanicParts.orderingRestrictedHint',
                  'Purchase orders are created by your foreman or service advisor.',
                )}
              >
                <Chip
                  size="small"
                  icon={<BlockIcon sx={{ fontSize: 14 }} />}
                  label={t('mechanicParts.poManagedByForeman', 'PO by foreman')}
                  color="default"
                  variant="outlined"
                  sx={{ cursor: 'help', fontSize: '0.7rem' }}
                />
              </Tooltip>
            )}
          </Stack>
        </Box>
      )}

      {neededParts.length > 0 && hasReservations && <Divider sx={{ my: 1 }} />}

      {/* ── Reserved (stock held, ready to install) ──────── */}
      {hasReservations && (
        <Box sx={{ mb: 1.5 }}>
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.75 }}>
            <InventoryIcon sx={{ fontSize: 14, color: 'info.main' }} />
            <Typography
              variant="caption"
              fontWeight={700}
              color="info.main"
              textTransform="uppercase"
            >
              {t('mechanicParts.sections.reserved', 'Reserved – ready to install')}
            </Typography>
            <Chip
              size="small"
              label={partsStatus.reservations.length}
              color="info"
              sx={{ height: 18, fontSize: '0.65rem' }}
            />
          </Stack>

          <Stack spacing={0.5}>
            {partsStatus.reservations.map((reservation) => (
              <Stack
                key={reservation.reservationId}
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={1}
                sx={{
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  bgcolor: (theme) =>
                    theme.palette.mode === 'dark'
                      ? 'rgba(2,136,209,0.08)'
                      : 'info.50',
                  border: '1px solid',
                  borderColor: 'info.200',
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" noWrap>
                    {reservation.partName}
                  </Typography>
                  {reservation.partNumber && (
                    <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                      {reservation.partNumber}
                    </Typography>
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                  ×{reservation.quantity}
                </Typography>
                <PartStatusChip status="reserved" />
                {canConsume && (
                  <Stack direction="row" spacing={0.25}>
                    <Tooltip title={t('mechanicParts.actions.markInstalled', 'Mark installed')}>
                      <IconButton
                        size="small"
                        color="success"
                        onClick={() => { setInstallReservation(reservation); }}
                        aria-label={t('mechanicParts.actions.markInstalled')}
                        data-testid={`install-reservation-btn-${reservation.reservationId}`}
                      >
                        <CheckCircleOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('mechanicParts.actions.release', 'Release')}>
                      <IconButton
                        size="small"
                        onClick={() => { handleRelease(reservation.reservationId); }}
                        aria-label={t('mechanicParts.actions.release')}
                        data-testid={`release-reservation-btn-${reservation.reservationId}`}
                      >
                        <UndoIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                )}
              </Stack>
            ))}
          </Stack>
        </Box>
      )}

      {(neededParts.length > 0 || hasReservations) && inTransitParts.length > 0 && (
        <Divider sx={{ my: 1 }} />
      )}

      {/* ── On order / in transit ────────────────────────── */}
      {inTransitParts.length > 0 && (
        <Box sx={{ mb: 1.5 }}>
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.75 }}>
            <LocalShippingIcon sx={{ fontSize: 14, color: 'info.main' }} />
            <Typography
              variant="caption"
              fontWeight={700}
              color="info.main"
              textTransform="uppercase"
            >
              {t('mechanicParts.sections.ordered', 'Ordered / in transit')}
            </Typography>
            <Chip
              size="small"
              label={inTransitParts.length}
              color="info"
              sx={{ height: 18, fontSize: '0.65rem' }}
            />
          </Stack>

          <Stack spacing={0.5}>
            {inTransitParts.map((part) => (
              <Stack
                key={part.lineId}
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={1}
                sx={{
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  bgcolor: (theme) =>
                    theme.palette.mode === 'dark'
                      ? 'rgba(2,136,209,0.08)'
                      : 'info.50',
                  border: '1px solid',
                  borderColor: 'info.200',
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" noWrap>
                    {part.partName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {part.supplierName}
                    {part.expectedDeliveryDate &&
                      ` — ${formatDate(part.expectedDeliveryDate)}`}
                  </Typography>
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ flexShrink: 0 }}
                >
                  {part.quantityReceived}/{part.quantityOrdered}
                </Typography>
                {/* In transit means quantityReceived < quantityOrdered, so a
                    positive receipt is only partial and stays backordered (#3568). */}
                <PartStatusChip
                  status={part.quantityReceived > 0 ? 'backordered' : 'ordered'}
                />
              </Stack>
            ))}
          </Stack>
        </Box>
      )}

      {(neededParts.length > 0 || hasReservations || inTransitParts.length > 0) &&
        arrivedParts.length > 0 && <Divider sx={{ my: 1 }} />}

      {/* ── Arrived (ready to install) ───────────────────── */}
      {arrivedParts.length > 0 && (
        <Box sx={{ mb: 1 }}>
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.75 }}>
            <CheckCircleOutlineIcon sx={{ fontSize: 14, color: 'success.main' }} />
            <Typography
              variant="caption"
              fontWeight={700}
              color="success.main"
              textTransform="uppercase"
            >
              {t('mechanicParts.sections.arrived', 'Arrived – ready to install')}
            </Typography>
            <Chip
              size="small"
              label={arrivedParts.length}
              color="success"
              sx={{ height: 18, fontSize: '0.65rem' }}
            />
          </Stack>

          <Stack spacing={0.5}>
            {arrivedParts.map((part) => (
              <Stack
                key={part.lineId}
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={1}
                sx={{
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  bgcolor: (theme) =>
                    theme.palette.mode === 'dark'
                      ? 'rgba(46,125,50,0.08)'
                      : 'success.50',
                  border: '1px solid',
                  borderColor: 'success.200',
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" noWrap>
                    {part.partName}
                  </Typography>
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ flexShrink: 0 }}
                >
                  ×{part.quantityReceived}
                </Typography>
                <PartStatusChip status="arrived" />
              </Stack>
            ))}
          </Stack>
        </Box>
      )}

      {/* ── Awaiting consumption confirmation (work_order_parts) ── */}
      {canConsume && consumableParts.length > 0 && (
        <Box sx={{ mb: 1 }} data-testid="consumable-parts-section">
          {(neededParts.length > 0 || hasReservations || onOrderParts.length > 0) && <Divider sx={{ my: 1 }} />}
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.75 }}>
            <CheckCircleOutlineIcon sx={{ fontSize: 14, color: 'success.main' }} />
            <Typography
              variant="caption"
              fontWeight={700}
              color="success.main"
              textTransform="uppercase"
            >
              {t('mechanicParts.sections.toConfirm')}
            </Typography>
            <Chip
              size="small"
              label={consumableParts.length}
              color="success"
              sx={{ height: 18, fontSize: '0.65rem' }}
            />
          </Stack>
          <Button
            size="small"
            variant="contained"
            color="success"
            startIcon={<CheckCircleOutlineIcon />}
            onClick={() => setConsumeDialogOpen(true)}
            sx={{ fontSize: '0.75rem' }}
            data-testid="mark-consumed-btn"
          >
            {t('mechanicParts.actions.markConsumed', 'Mark installed / leftover')}
          </Button>
        </Box>
      )}
    </Box>
  );

  const headerBadges = (
    <>
      {neededParts.length > 0 && (
        <Chip
          size="small"
          label={`${String(neededParts.length)} ${t('mechanicParts.needed', 'needed')}`}
          color="warning"
          sx={{ height: 18, fontSize: '0.65rem' }}
        />
      )}
      {hasReservations && (
        <Chip
          size="small"
          label={`${String(partsStatus.reservations.length)} ${t('mechanicParts.reserved', 'reserved')}`}
          color="info"
          sx={{ height: 18, fontSize: '0.65rem' }}
        />
      )}
      {arrivedParts.length > 0 && (
        <Chip
          size="small"
          label={`${String(arrivedParts.length)} ${t('mechanicParts.arrived', 'arrived')}`}
          color="success"
          sx={{ height: 18, fontSize: '0.65rem' }}
        />
      )}
      {inTransitParts.length > 0 && (
        <Chip
          size="small"
          label={`${String(inTransitParts.length)} ${t('mechanicParts.inTransit', 'ordered')}`}
          color="info"
          sx={{ height: 18, fontSize: '0.65rem' }}
        />
      )}
    </>
  );

  return (
    <>
      {collapsible ? (
        <>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ cursor: 'pointer', userSelect: 'none' }}
            onClick={() => setExpanded((v) => !v)}
            data-testid="mechanic-parts-panel-header"
          >
            <InventoryIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography
              variant="caption"
              fontWeight={700}
              color="text.secondary"
              textTransform="uppercase"
            >
              {t('mechanicParts.panelTitle', 'Parts')}
            </Typography>
            {headerBadges}
            <Tooltip title={expanded ? tc('collapse') : tc('expand')}>
              <IconButton
                size="small"
                sx={{
                  ml: 'auto',
                  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s',
                  p: 0.25,
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setExpanded((v) => !v);
                }}
                aria-label={expanded ? tc('collapse') : tc('expand')}
              >
                <ExpandMoreIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
          <Collapse in={expanded} unmountOnExit>
            <Box sx={{ mt: 1 }}>{panelContent}</Box>
          </Collapse>
        </>
      ) : (
        <>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <InventoryIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography
              variant="subtitle2"
              fontWeight={700}
              color="text.secondary"
              textTransform="uppercase"
            >
              {t('mechanicParts.panelTitle', 'Parts')}
            </Typography>
            {headerBadges}
          </Stack>
          {panelContent}
        </>
      )}

      {/* ── Dialogs ─────────────────────────────────────────── */}
      <RequestPartDialog
        open={requestDialogOpen}
        onClose={() => setRequestDialogOpen(false)}
        workOrderId={workOrderId}
      />

      {neededParts.length > 0 && (
        <OrderPartsDialog
          open={orderDialogOpen}
          onClose={() => setOrderDialogOpen(false)}
          workOrderId={workOrderId}
          neededParts={neededParts}
        />
      )}

      {consumableParts.length > 0 && (
        <PartsConsumptionDialog
          key={consumableParts.map((p) => p.id).join(',')}
          open={consumeDialogOpen}
          onClose={() => setConsumeDialogOpen(false)}
          workOrderId={workOrderId}
          parts={consumableParts}
        />
      )}

      {reservingPart && (
        <PartNeedReserveDialog
          open={!!reservingPart}
          onClose={() => { setReservingPart(null); }}
          partName={reservingPart.partName}
          neededQuantity={reservingPart.quantity}
          currentStock={reservingPart.availableStock}
          onReserve={handleReserveSubmit}
          isLoading={reservePart.isPending}
        />
      )}

      <ConfirmDialog
        open={!!installReservation}
        title={t('mechanicParts.confirmInstall.title', 'Mark part installed?')}
        message={
          installReservation
            ? t('mechanicParts.confirmInstall.message', {
                count: installReservation.quantity,
                partName: installReservation.partName,
                defaultValue: 'This will remove {{count}} × {{partName}} from stock and close the reservation.',
              })
            : ''
        }
        confirmText={t('mechanicParts.actions.markInstalled', 'Mark installed')}
        confirmColor="success"
        onConfirm={handleInstallConfirm}
        onCancel={() => { setInstallReservation(null); }}
        loading={consumeReservation.isPending}
      />
    </>
  );
}
